import _ from 'lodash'

/**
 * Helper for UI Router resolves that fetch TMR Resources.
 *
 * @alias TmrResourceResolve
 * @type {ngFactory}
 */
export class TmrResourceResolve {
  static ngType = 'service'

  constructor($injector, TmrPaging, TmrSearchFormFilters) {
    'ngInject'
    Object.assign(this, { $injector, TmrPaging, TmrSearchFormFilters })
  }

  /**
   * Checks if there is any active search filter in the current destination state
   * Used only in transitions.
   */
  hasActiveFilters($transition$) {
    return (
      this.TmrSearchFormFilters.extractActiveFiltersFromState(
        $transition$.to(),
        $transition$.params()
      ).length > 0
    )
  }

  /**
   * Builds a paged search query from a resourceList route transition.
   * Used by {@link tmrResourceListRoutesConfig} and custom resourceList routes.
   *
   * @example <caption>In route.resolve definition</caption>
   * ($transition$, TmrResourceResolve) => TmrResourceResolve.query($transition$)()
   *
   * @example <caption>With a custom query parameter</caption>
   * ($transition$, TmrResourceResolve) => TmrResourceResolve.query($transition$)({ someCustomParam: '123' })
   *
   * @returns {function} A function that executes the query. Accepts an `overrideParams` argument
   * for query customizations. Returns a promise.
   */
  query($transition$) {
    const {
      resourceType,
      resourceList: { searchParameters, hiddenSearchParameters, requireFilter },
    } = $transition$.to()
    const { searchAction, ...stateParams } = $transition$.params()

    if (requireFilter === true && !this.hasActiveFilters($transition$)) {
      return () => []
    }

    const queryParams = this.TmrPaging.resolveParams(
      $transition$,
      _.pick(stateParams, searchParameters, hiddenSearchParameters)
    )

    // The action will end up being something like User.search or Place.query
    const action = this.$injector.get(resourceType)[searchAction]

    return (overrideParams = {}) =>
      action(Object.assign(queryParams, overrideParams)).$promise
  }

  /**
   * Builds a count query from a resourceList route transition.
   * Used by {@link tmrResourceListRoutesConfig} and custom resourceList routes.
   *
   * @example <caption>In route.resolve definition</caption>
   * ($transition$, TmrResourceResolve) => TmrResourceResolve.count($transition$)()
   *
   * @example <caption>With a custom query parameter</caption>
   * ($transition$, TmrResourceResolve) => TmrResourceResolve.count($transition$)({ someCustomParam: '123' })
   *
   * @returns {function} A function that executes the query. Accepts an `overrideParams` argument
   * for query customizations. Returns a promise.
   */
  count($transition$) {
    const {
      resourceType,
      resourceList: { searchParameters, hiddenSearchParameters, requireFilter },
    } = $transition$.to()
    const { countAction, ...stateParams } = $transition$.params()

    if (requireFilter === true && !this.hasActiveFilters($transition$)) {
      return () => null
    }

    const queryParams = _.pick(
      stateParams,
      searchParameters,
      hiddenSearchParameters
    )
    const action = this.$injector.get(resourceType)[countAction]

    return (overrideParams = {}) =>
      action(Object.assign(queryParams, overrideParams))
  }
}
